import { useMemo, type ReactNode } from 'react';
import { useBrainStore } from '@/state/brainStore';
import InfoTip from '@/components/InfoTip';
import type { StudioId } from '@/domain/types';
import { rebookAttribution, type RebookOutcomeCounts } from '@/domain/reportingEngine';
import clsx from 'clsx';

type OutcomeKey = 'rebooked' | 'declined' | 'deferred';

function total(c: RebookOutcomeCounts) {
  return c.rebooked + c.declined + c.deferred;
}

/**
 * Surfaces the rebook attribution data captured at floor check-out.
 *
 * KPI tiles → overall rebook rate, deposit-applied rate, sales-deferred rate,
 * sales conversion (of deferred → booked). Stacked bar shows per-studio split
 * across the three outcomes. Per-staff table ranks artists + managers by who
 * facilitated the most rebooks (Outcome A).
 */
export default function RebookAttributionPanel({
  days,
  studioId,
}: {
  days: readonly string[];
  studioId?: StudioId | null;
}) {
  const studios = useBrainStore((s) => s.studios);
  // Subscribe to bookings so the memo recomputes when checkout outcomes land;
  // the actual scoping (studio + date range) happens inside the selector.
  const bookings = useBrainStore((s) => s.bookings);

  const data = useMemo(
    () => rebookAttribution(days, studioId),
    // `bookings` is an intentional recompute trigger — the selector reads the
    // store itself, so eslint can't see the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings],
  );

  const overallTotal = total(data.overall);
  const rebookRate = overallTotal === 0 ? 0 : (data.overall.rebooked / overallTotal) * 100;
  const declinedRate = overallTotal === 0 ? 0 : (data.overall.declined / overallTotal) * 100;
  const deferredRate = overallTotal === 0 ? 0 : (data.overall.deferred / overallTotal) * 100;
  const salesConvRate =
    data.overall.deferred === 0 ? 0 : (data.overall.deferredBooked / data.overall.deferred) * 100;

  return (
    <section className="panel p-4 max-w-6xl" data-testid="reporting-rebook-attribution">
      <header className="mb-3">
        <h2 className="heading-serif text-lg text-cream">Rebook attribution</h2>
        <p className="font-serif text-cream-soft text-sm mt-1">
          What&apos;s happening in the chair at checkout, for the selected studio and date range. Captured from{' '}
          {overallTotal} completed sessions.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="rebook-kpi-tiles">
        <KpiTile
          label="Rebook rate"
          value={`${rebookRate.toFixed(0)}%`}
          tone="mint"
          sub={`${data.overall.rebooked} of ${overallTotal}`}
          testid="kpi-rebook"
          info="Share of completed sessions where the next booking was made in the chair at checkout (Outcome A). The headline the leaderboard ranks on."
        />
        <KpiTile
          label="Deposit applied"
          value={`${declinedRate.toFixed(0)}%`}
          tone="blood"
          sub={`${data.overall.declined} of ${overallTotal}`}
          testid="kpi-declined"
          info="Share where the client did not rebook in the chair, so their deposit was applied to that session and they left without a next date (Outcome B)."
        />
        <KpiTile
          label="Sales deferred"
          value={`${deferredRate.toFixed(0)}%`}
          tone="gold"
          sub={`${data.overall.deferred} of ${overallTotal}`}
          testid="kpi-deferred"
          info="Share where the floor handed the rebook to the sales team to follow up later rather than closing it in the chair (Outcome C)."
        />
        <KpiTile
          label="Sales conv. rate"
          value={`${salesConvRate.toFixed(0)}%`}
          tone="cream"
          sub={`${data.overall.deferredBooked} of ${data.overall.deferred} deferred booked`}
          testid="kpi-sales-conv"
          info="Of the sales-deferred sessions, the share the sales team then converted into a booking."
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-7 panel-dark p-3" data-testid="rebook-bar-by-studio">
          <p className="label-small-caps mb-3 inline-flex items-center gap-1">
            Per studio · outcome split
            <InfoTip>Each studio&apos;s completed sessions split across the three checkout outcomes: rebooked in chair, deposit applied, and sales-deferred.</InfoTip>
          </p>
          {Object.entries(data.byStudio).length === 0 ? (
            <p className="font-serif text-cream-soft text-sm">
              No completed sessions with rebook outcomes yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {Object.entries(data.byStudio).map(([sid, c]) => {
                const t = total(c);
                return (
                  <li key={sid} data-testid={`rebook-bar-${sid}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-inter text-sm text-cream">
                        {studios[sid as StudioId]?.name ?? sid}
                      </span>
                      <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                        {t} sessions
                      </span>
                    </div>
                    <StackedBar counts={c} total={t} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="col-span-12 md:col-span-5 panel-dark p-3" data-testid="rebook-staff-table">
          <p className="label-small-caps mb-3 inline-flex items-center gap-1">
            Top facilitators (Outcome A)
            <InfoTip>Staff ranked by how many in-chair rebooks they facilitated — the artist or floor manager credited at checkout.</InfoTip>
          </p>
          {data.staffRows.length === 0 ? (
            <p className="font-serif text-cream-soft text-sm">
              No rebooks attributed yet.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left py-1.5">Staff</th>
                  <th className="text-left py-1.5">Role</th>
                  <th className="text-right py-1.5">Rebooks</th>
                </tr>
              </thead>
              <tbody>
                {data.staffRows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-gold-dim/15"
                    data-testid={`rebook-staff-${r.id}`}
                  >
                    <td className="py-1.5 font-inter text-cream">{r.name}</td>
                    <td className="py-1.5 font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                      {r.role}
                    </td>
                    <td className="py-1.5 text-right font-inter text-gold-bright">{r.rebooks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}

function KpiTile({
  label,
  value,
  sub,
  tone,
  testid,
  info,
}: {
  label: string;
  value: string;
  sub: string;
  tone: 'mint' | 'blood' | 'gold' | 'cream';
  testid: string;
  info?: ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      className={clsx(
        'panel-dark p-3 border-l-2',
        tone === 'mint' && 'border-mint/60',
        tone === 'blood' && 'border-blood/60',
        tone === 'gold' && 'border-gold-bright/60',
        tone === 'cream' && 'border-cream-soft/40',
      )}
    >
      <p className="label-small-caps inline-flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p
        className={clsx(
          'font-cinzel text-3xl mt-1',
          tone === 'mint' && 'text-mint',
          tone === 'blood' && 'text-blood',
          tone === 'gold' && 'text-gold-bright',
          tone === 'cream' && 'text-cream',
        )}
      >
        {value}
      </p>
      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">{sub}</p>
    </div>
  );
}

function StackedBar({ counts, total: t }: { counts: RebookOutcomeCounts; total: number }) {
  if (t === 0) {
    return <div className="h-2 rounded bg-ink-soft/40" />;
  }
  const seg = (n: number) => (n / t) * 100;
  const parts: Array<{ pct: number; tone: OutcomeKey }> = [
    { pct: seg(counts.rebooked), tone: 'rebooked' },
    { pct: seg(counts.declined), tone: 'declined' },
    { pct: seg(counts.deferred), tone: 'deferred' },
  ];
  return (
    <div className="flex h-2 rounded overflow-hidden bg-ink-soft/40">
      {parts.map((p, i) =>
        p.pct === 0 ? null : (
          <div
            key={i}
            style={{ width: `${p.pct}%` }}
            className={clsx(
              p.tone === 'rebooked' && 'bg-mint',
              p.tone === 'declined' && 'bg-blood',
              p.tone === 'deferred' && 'bg-gold-bright',
            )}
            title={`${p.tone} · ${p.pct.toFixed(0)}%`}
          />
        ),
      )}
    </div>
  );
}
